// Make-up credits, as a ledger rather than a counter.
//
// MMS shows three numbers on the student profile: Booked, Available, Total.
// Three counters on the student document would not survive offline editing:
// two devices issuing a credit offline would each write total=1.
//
// So credits use the same shape as the money ledger: append-only entries, folded
// on read. Issuing is keyed to the attendance decision that caused it (see
// core/src/ids.rs) so the same absence cannot mint two credits, and spending is
// keyed the same way by `makeupConsumeId` (#303).
//
// ── A credit is a length of time, the way MyMusicStaff's is (#352) ─────────
//
// In MMS, changing a student's Lesson Settings duration recomputes their
// balance in place — five 30-minute credits read **3.33** at 45 minutes and
// **2.50** at 60 — and a 45-minute event against a 30-minute lesson books
// **1.5** credits. So the number a teacher sees is **stored minutes over that
// student's current lesson length**.
//
// **Every entry carries `durationMin`** — how long the lesson was. The fold
// sums those minutes; `creditSummary` divides by the student's current lesson
// length, so a duration change moves every number on the card and touches no
// document. Missing a 60-minute lesson and booking a 30-minute make-up spends
// **half** a credit.
//
// `defaultDurationMin` is mandatory (#352), at both Duration fields and in
// `firestore.rules`, as MMS requires it. `studentDurationMin` in ./people.js
// returns `null` for a document without one, and this file answers a null
// denominator with `null`.

import { todayISO } from './clock.js';
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { isActive, tombstoneOf } from './tombstone.js';

// ── Adapters ───────────────────────────────────────────────────────────────
//
// Every rule below is `core/src/makeup.rs` — the fold, booking, lapse, the
// cap, and Set Make-Up Credits' plan, document and sentences — so the phones'
// balances are the web's to the minute. What this file keeps is the coercion
// into the core's `MakeupEntry` (a stored number read with `Number()`, the
// tombstone reduced as `tombstone.js` reduces it), the policy, and the
// module-scope tables (`CREDIT_KINDS`, the bounds, `ADJUST_SOURCE`,
// `BOOKING_HORIZON_DAYS`), pinned to the core's by
// `test/domain/lessonCore.test.js`. `core/src/lesson_vectors.json` holds the
// recorded answers.

const str = (v) => (v == null ? null : String(v));
const num = (v) => (v == null ? null : Number(v));

// A stored `makeupEntries` document as the core's `MakeupEntry`.
function makeupEntryInput(e) {
  return {
    id: str(e?.id),
    kind: str(e?.kind),
    durationMin: num(e?.durationMin),
    occurredOn: str(e?.occurredOn),
    ...tombstoneOf(e),
    sourceType: str(e?.sourceType),
    slot: num(e?.slot),
  };
}

// The studio's policy as the core's `MakeupPolicy`: `null` is no cap / never.
function policyInput(policy) {
  return {
    maxCredits: num(policy?.maxCredits),
    expireAfterDays: num(policy?.expireAfterDays),
  };
}

// The **sign** each kind contributes. A credit is a length of time (#352):
// the size comes from the entry's own `durationMin` and these say only which
// way it points. `book` and `unbook` are 0 here and are counted by
// `bookedMinutes` instead.
export const CREDIT_KINDS = {
  // Granted by a credit-issuing attendance status.
  issue:  { id: 'issue',  delta: +1 },
  // Reserved against a specific future lesson — spent, but not yet taught.
  //
  // **Never written (#363).** A booking is derived from the calendar by
  // `bookedMinutesFor` below. These two kinds stay because `firestore.rules`
  // admits a document carrying either, and the fold counts what it admits.
  book:   { id: 'book',   delta: 0 },
  // A booked make-up that was released without being taught.
  unbook: { id: 'unbook', delta: 0 },
  // Redeemed: the make-up lesson happened. **Written by** the three
  // `Use Make-Up Credit` statuses (#303), through `setAttendance` in
  // data/useAttendance.jsx, keyed `makeupConsumeId(occurrenceId, studentId)`.
  // `lapsedMinutes`'s `spent` total below is `consume` and only `consume`.
  consume:{ id: 'consume', delta: -1 },
  // **Revoked by hand, and only that.** Lapsing under the studio's expiry
  // policy writes nothing — it is folded on read by `lapsedMinutes` below.
  // Written by `setCredits` in data/useMakeup.jsx when a teacher types a
  // lower total.
  expire: { id: 'expire', delta: -1 },
  // **A shortfall charged for (#543).** Written by the server alone, when a
  // Family Portal reschedule taken without enough credit is settled: it
  // brings the balance back up by the minutes the family was charged for.
  // Added after the studio's cap and never lapsed (core/src/makeup.rs,
  // "Reschedule shortfalls").
  offset: { id: 'offset', delta: +1 },
};

// Two fields, and the boolean is the one this believes — see ledger.js's
// isActive. On one field alone, a make-up credit retracted offline reads as
// granted until the write is acknowledged (#70). Imported as well as
// re-exported: a bare `export … from` creates no local binding.
export { isActive };

// How long the lesson behind one entry was, in minutes.
//
// **Zero for an entry that does not say.** The writers all stamp it —
// `setAttendance` in data/useAttendance.jsx on both the issue and the consume,
// `markAttendance` in the phone's StudioRepository.kt on the same two, and
// `setCredits` in data/useMakeup.jsx on a hand adjustment — so an entry
// without one is a document this app did not write. Zero rather than "one
// lesson's worth", so that every entry folds as time and never as a count.
export function entryMinutes(entry) {
  return coreSync().makeupEntryMinutes(makeupEntryInput(entry || {}));
}

// Minutes ever issued, less those consumed or revoked. This is the student's
// lifetime entitlement net of what has been used, in minutes.
export function totalMinutes(entries) {
  return coreSync().makeupTotalMinutes(entries.map(makeupEntryInput));
}

// The ledger's book/unbook pairs. **Nothing writes one of these (#363)** — a
// booking is derived by `bookedMinutesFor` directly below. The fold stays
// because a document of either kind is still admissible under
// `firestore.rules`; it answers 0 for a studio this app wrote.
export function bookedMinutes(entries) {
  return coreSync().makeupBookedMinutes(entries.map(makeupEntryInput));
}

// How far forward a booking is looked for. See `bookedMinutesFor`.
export const BOOKING_HORIZON_DAYS = 365;

// The window `MakeupProvider` expands the calendar over, as `[start, end]`.
//
// **Here rather than inline in data/useMakeup.jsx (#372)**, so a pure test
// can pin it: test/domain/makeup.test.js holds a flagged lesson 365 days out
// inside the window and one at 366 outside.
//
// It takes `asOf` rather than calling `todayISO()`: every student's summary
// in one render has to agree about what today is.
export function bookingWindow(asOf) {
  const { from, to } = coreSync().bookingWindow(asOf);
  return [from, to];
}

// ── A flagged occurrence books a credit forward (#363, #317) ────────────────
//
// In MMS, ticking `This event requires a make-up credit` on a future
// occurrence moves the student rail from `Booked: 0 | Available: 0 | Total: 0`
// to `Booked: 1.5 | Available: -1.5 | Total: 0`, with no refusal, and
// unticking puts `Booked` back to 0. This copies that, silence included.
//
// **Derived, not written.** A ledger of `book`/`unbook` pairs would match the
// untick only if every untick, attendee change, occurrence deletion and series
// edit appended the opposite entry. A booking is a pure function of the
// calendar as it stands, as the cap and the expiry window are of the policy,
// and all three are folded here; nothing is written, so two offline devices
// have nothing to converge.
//
// ── What counts ────────────────────────────────────────────────────────────
//
//   * the occurrence carries the flag,
//   * this student is one of its attendees,
//   * it is **still unrecorded** — a lesson already marked has either spent the
//     credit (a `consume` entry, which `totalMinutes` has already taken off) or
//     decided not to. MMS: "a flagged, *unrecorded* future occurrence shows up
//     as `Booked`".
//   * it is **today or later**. Whether a past unmarked flagged lesson books
//     in MMS is not known (#286); today-or-later is the narrower reading.
//
// ── The horizon (#372) ─────────────────────────────────────────────────────
//
// MMS stores a counter, so it has no horizon. A weekly series with no end date
// has no last occurrence, so the expansion needs a window, and
// `BOOKING_HORIZON_DAYS` is a year. Undercounting bookings overstates
// `available` and offers a credit that is already promised; overcounting is
// bounded by how far ahead anybody schedules. A flagged lesson more than a
// year out is not counted. `PACKAGE_FORECAST_DAYS_FORWARD` (90) is a
// different question and is not reused.
//
// ── An occurrence with no duration ─────────────────────────────────────────
//
// Books nothing, and `lessonMin` is not a parameter (#374, #365): falling back
// to the student's own lesson length would read such a booking as exactly
// 1.00. `occurrencesBetween` resolves a missing duration to 30 in
// core/src/schedule.rs, so a zero means the caller did not come off the
// calendar fold.
export function bookedMinutesFor(occurrences, {
  studentId, statusFor, asOf = todayISO(), except = null,
} = {}) {
  if (!studentId || typeof statusFor !== 'function') return 0;
  const flagged = (occurrences || []).filter((o) => o?.requiresMakeupCredit === true);
  return coreSync().bookedMinutesFor(flagged.map((o) => ({
    id: String(o.id ?? ''),
    date: str(o.date),
    studentIds: (o.studentIds || []).map(String),
    requiresMakeupCredit: true,
    durationMin: num(o.durationMin),
    status: str(statusFor(o.id, studentId)),
  })), String(studentId), asOf, str(except));
}

// What the student can actually spend right now, in minutes.
//
// **Not clamped at zero (#363).** MMS lets the balance go negative and says
// nothing, so the arithmetic is `Available = Total − Booked`, the identity the
// student rail shows. `statuses_for` in core/src/attendance.rs gates the
// spending statuses on `availableMin >= occurrenceMin` (#406), so a negative
// balance hides those rows as a zero does.
//
// This overload takes entries alone, so it sees only the ledger half of
// `booked`. `creditSummary` sees both.
export function availableMinutes(entries) {
  return coreSync().makeupAvailableMinutes(entries.map(makeupEntryInput));
}

// Minutes as the number a teacher sees: a quotient over the student's current
// lesson length, rounded to two places.
//
// **Two places because MMS shows two** (`3.33`, `2.50`). The rounding is
// applied to the *value* and not only to its rendering, so the number in `Set
// Make-Up Credits` saved unchanged comes back unchanged: 3.33 × 45 is 149.85,
// which rounds to the 150 minutes already stored, so nothing is written.
//
// `null` for a student with no lesson length (`studentDurationMin` in
// ./people.js, #352); the screens dash it (see `formatCredits`).
export function creditsFrom(minutes, lessonMin) {
  return coreSync().creditsFrom(Number(minutes), typeof lessonMin === 'number' ? lessonMin : null) ?? null;
}

// What `total` credits reads back as once saved, at `lessonMin` (#586): the
// same whole-minute rounding `setCredits` commits, read without writing
// anything. MMS rewrites the Set Make-Up Credits box to this figure the
// moment it loses focus, before Save — typing `0.75` at a 30-minute lesson
// shows `0.77`. `null` — box left as typed — for a total that is not a
// finite number or a student with no lesson length to round against.
export function creditsAsSaved(total, lessonMin) {
  const n = Number(total);
  if (!Number.isFinite(n)) return null;
  return coreSync().creditsAsSaved(n, typeof lessonMin === 'number' ? lessonMin : null) ?? null;
}

// The one place a credit count becomes text, dash for "cannot say" included.
// `decimal` is the studio's Region's separator (`useLocale().decimalSeparator`):
// `1,5` credits in Germany (#549). Required — the core refuses a call without
// it.
export function formatCredits(credits, decimal) {
  return coreSync().formatCredits(credits == null ? null : Number(credits), decimal);
}

// ─── The studio's make-up policy, applied ──────────────────────────────────
//
// The two settings are `maxCredits` and `expireAfterDays` on
// `studio.makeup` (src/domain/studio.js). MMS puts them on **Business Settings
// → Policies**, under "Make-Up Credit Policy" — *"These settings allow you to
// fine-tune your make-up policy"* — with "Limit number of make-up credits" and
// "Expire unused make-up credits". Here they are on the Email & SMS panel
// (MessagingPanel.jsx), beside the rest of the `makeup` group; the Policies
// panel holds the portal's booking rules.
//
// ── Both are folded on read rather than written into the ledger ────────────
//
// **The cap does not refuse a credit; it caps the fold.** `setAttendance` in
// data/useAttendance.jsx writes the credit in one batch with the attendance
// record, the charge and any payment, so a refused credit would refuse the
// attendance decision. The write also happens offline against a possibly-stale
// ledger, so two devices could each see room for one more. Capping the fold
// keeps the ledger a record of what the teacher decided: raising the cap
// brings suppressed credits back and lowering it hides them again.
//
// **Expiry is folded too, and is not what writes an `expire` entry.** Age is a
// pure function of when a credit was issued, the policy and today's date; a
// stored expiry would be wrong the moment `expireAfterDays` changed. `expire`
// means **revoked by hand** — a decision that has to survive a policy change.
// `Set Make-Up Credits` (#178) writes it: `setCredits` in data/useMakeup.jsx
// appends one `expire` per credit the typed total is short of. So the lapse
// fold must not count it as spending an issue (see `lapsedMinutes` below).
//
// ── The cap (`with_cap` in core/src/makeup.rs) ─────────────────────────────
//
// `null` is "no cap" and `0` is "this studio issues none" — different values,
// kept apart by normaliseMakeupPolicy and by firestore.rules, so the test is
// `== null` and never `|| `.
//
// **The setting counts credits, and the fold is in minutes** (#352), so the
// ceiling is `maxCredits` lessons of this student's length. It is applied in
// minutes so that `totalMin` — which `setCredits` measures its delta against —
// is capped by the same arithmetic the teacher sees.
//
// A student with no lesson length has no cap applied to their minutes; every
// credit number for such a student is `null` (see `creditSummary`), so an
// uncapped total cannot reach a screen.

// Re-exported so this module's callers do not each pick a "today".
//
// Not `toISOString().slice(0, 10)`: that is the *UTC* day, not the teacher's.
// At 11:30pm on 2 September in Los Angeles it returns 2026-09-03, and credits
// would lapse a day early. domain/clock.js reads today off the wall clock.
export { todayISO };

// The **minutes** of issued credit that have gone unused past the studio's
// expiry window. A credit is a length of time (#352) and an entry can lapse
// in part, so there is no list of whole entries to return.
//
// Consumption is matched to issues oldest-first (the ledger carries no link
// between them): a student redeeming a make-up spends the credit they have
// held longest, which expires the fewest. `id` breaks a same-day tie, so every
// device folds alike.
//
// A credit is expired on the day it reaches `expireAfterDays` days of age:
// `occurredOn <= asOf - expireAfterDays`, compared as date-only strings.
//
// **`consume` only, and `expire` not.** `consume` is a redemption — one of
// the held credits is spent. `expire` is a hand adjustment that the total
// already takes off; counting it here too would also remove the oldest issue
// from the lapse candidates, so a revocation would cancel itself out whenever
// anything had lapsed (#178). With `expire` out, `creditSummary` subtracts the
// lapsed and (through the total) the revocations, clamping at 0.
//
// **A credit can be spent in part** (#352): a 30-minute make-up against a
// 60-minute credit spends half of it, so the walk carries a running balance of
// unmatched consumed minutes and leaves a remainder on the issue it stops in
// the middle of.
export function lapsedMinutes(entries, policy = {}, asOf = todayISO()) {
  return coreSync().makeupLapsedMinutes(entries.map(makeupEntryInput), policyInput(policy), asOf);
}

// The three-number summary the profile and the students-list column both
// render, under the studio's policy and against this student's lesson length.
//
// `policy` defaults to `{}` — no cap, no expiry.
//
// `lessonMin` is the student's current lesson length: `student.durationMin` on
// a row from data/useStudents.jsx (`defaultDurationMin`, read through
// `studentDurationMin` in ./people.js). Positional and second rather than in
// `policy`, since the policy is the studio's and the length the student's, and
// with no default.
//
// ── What comes back ───────────────────────────────────────────────────────
//
// Both units:
//
//   * `total`, `booked`, `available`, `expired` are **credits** — the numbers
//     on the card, `null` where the student has no lesson length to divide by.
//   * `totalMin`, `bookedMin`, `availableMin`, `expiredMin` are the minutes
//     behind them. `setCredits` in data/useMakeup.jsx measures its delta in
//     these, because a delta measured in a rounded quotient would drift every
//     time a teacher saved.
//
// The entries hold minutes and this function holds the divisor, so a duration
// change moves these numbers and writes nothing.
//
// **The spending gate reads `availableMin`, not `available`** (#364, #406):
// MMS offers the three spending statuses when the available **minutes** are
// at least the occurrence's own length (`statuses_for` in
// core/src/attendance.rs). The rounded quotient is for printing only; a gate
// over it would decide a 15-minute lesson by a rounding.
//
// ── The fifth argument: minutes booked off the calendar (#363) ────────────
//
// `bookedMinutesFor` above produces it. It is summed with the ledger's own
// `book`/`unbook` fold, which nothing writes — two sources for one quantity.
// It defaults to 0 so a ledger-only caller reads as ledger-only;
// `test/domain/moneyReaderShape.test.js` asserts that every product call site
// passes it.
//
// `available` is `Total − Booked`, unclamped (#363). Bookings are not capped:
// a booking against a future lesson is a commitment to a family. `expired` is
// surfaced so a screen can say why a student's total dropped.
export function creditSummary(
  entries, lessonMin, policy = {}, asOf = todayISO(), bookedFromOccurrencesMin = 0,
) {
  const s = coreSync().creditSummary(
    entries.map(makeupEntryInput),
    typeof lessonMin === 'number' ? lessonMin : null,
    policyInput(policy),
    asOf,
    Number(bookedFromOccurrencesMin),
  );
  return {
    total: s.total ?? null,
    booked: s.booked ?? null,
    available: s.available ?? null,
    expired: s.expired ?? null,
    totalMin: s.totalMin,
    bookedMin: s.bookedMin,
    availableMin: s.availableMin,
    expiredMin: s.expiredMin,
  };
}

// ── Set Make-Up Credits ────────────────────────────────────────────────────
//
// Here rather than in `setCredits` in src/data/useMakeup.jsx and its drawer,
// so a phone's Set Make-Up Credits writes the same entries and says the same
// things. The reasons for the two bounds are in useMakeup.jsx, which
// re-exports them from here.

export const MAKEUP_CREDITS_MAX = 200;
export const MAKEUP_REASON_MAX = 499;

// `sourceType` on a credit a teacher set by hand.
export const ADJUST_SOURCE = 'manual';

// What pressing Save does: a refusal (`{ done: false, reason }`), nothing
// (`{ done: true, written: 0 }`), or the entries to write — `kind`, one
// `chunks` entry of minutes per document, the slot the first one takes, and
// how many credits that moves. `setCredits` has the argument for every step.
export function planSetCredits({
  entries = [], student, total, reason = '', policy = {}, asOf = todayISO(),
  signedIn = true, hasRead = true,
} = {}) {
  const plan = coreSync().planSetCredits(
    entries.map(makeupEntryInput),
    student ? { id: str(student.id), familyId: str(student.familyId), durationMin: num(student.durationMin) } : null,
    Number(total),
    str(reason || ''),
    policyInput(policy),
    asOf,
    Boolean(signedIn),
    Boolean(hasRead),
  );
  if (plan.refusal) return { done: false, reason: plan.refusal };
  if (!plan.kind) return { done: true, written: 0 };
  return {
    done: true,
    kind: plan.kind,
    firstSlot: plan.firstSlot,
    chunks: plan.chunks,
    note: plan.note,
    written: plan.written,
  };
}

// One hand adjustment's id: the issue id keyed on its slot.
export function makeupAdjustmentId(slot, studentId) {
  return coreSync().makeupAdjustmentId(Number(slot), String(studentId));
}

// One hand adjustment's document (`setCredits` says why each field is there).
// `familyId` is the student's family or null, by the rule every make-up
// entry is written with, an attendance save's included (`family_field` in
// core/src/makeup.rs).
export function makeupAdjustmentDocument({
  kind, slot, durationMin, student, occurredOn, uid, note,
}, serverTimestamp) {
  return documentFromRows(coreSync().makeupAdjustmentDocument(
    String(kind), Number(slot), Number(durationMin), String(student.id), str(student.familyId),
    String(occurredOn), String(uid), str(note),
  ), serverTimestamp);
}

// The box's own message: a total from 0 to the ceiling, or null.
export function makeupCreditsError(text, creditsMax = MAKEUP_CREDITS_MAX) {
  return coreSync().makeupCreditsError(String(text ?? ''), Number(creditsMax)) ?? null;
}

// One sentence per reason `planSetCredits` refuses with.
export function makeupRefusalMessage(reason, creditsMax = MAKEUP_CREDITS_MAX) {
  return coreSync().makeupRefusalMessage(str(reason), Number(creditsMax));
}

// The drawer's line on credits that lapsed, or '' when none did; the figure
// with the Region's `decimal` separator (#549).
export function makeupExpiredLine(expired, decimal) {
  return coreSync().makeupExpiredLine(typeof expired === 'number' ? expired : null, decimal);
}

// The drawer's warning when the typed total is above the studio's cap, or
// null. `maxCredits` is null for "no cap" and 0 for "none".
export function makeupCapHint(maxCredits, value) {
  return coreSync().makeupCapHint(maxCredits == null ? null : Number(maxCredits), Number(value)) ?? null;
}

// ── Reschedule shortfalls (#543) ────────────────────────────────────────────
//
// The rule — what a Family Portal reschedule taken without enough credit
// owes, earned credits paying off the earliest first, at the moved lesson's
// price in proportion — is `core/src/makeup.rs` ("Reschedule shortfalls").
// Only the Cloud Functions settle (functions/portal/settle.js); every screen
// folds what settling writes through `creditSummary` above, where an
// `offset` entry counts after the studio's cap.

/**
 * What each due reschedule owes now: `[{ requestId, minutes, amountMinor }]`.
 * `open` is `[{ requestId, date, minutes, shortMinutes, priceMinor }]`.
 */
export function rescheduleSettlements(availableMin, open, dueOn) {
  return [...coreSync().rescheduleSettlements(
    Number(availableMin),
    (open || []).map((o) => ({
      requestId: String(o.requestId),
      date: String(o.date),
      minutes: Number(o.minutes) || 0,
      shortMinutes: Number(o.shortMinutes) || 0,
      priceMinor: Number.isFinite(o.priceMinor) ? o.priceMinor : null,
    })),
    String(dueOn),
  )].map((x) => ({ requestId: x.requestId, minutes: x.minutes, amountMinor: x.amountMinor ?? null }));
}

/** The ids a settlement writes under, keyed on the reschedule. */
export const rescheduleOffsetId = (requestId) => coreSync().rescheduleOffsetId(String(requestId));
export const rescheduleChargeId = (requestId) => coreSync().rescheduleChargeId(String(requestId));

/** The charge's line: `moved` is the moved lesson as the studio's Region writes it. */
export const rescheduleChargeDescription = (moved) => coreSync().rescheduleChargeDescription(String(moved || ''));

/** The offset a settlement writes. */
export function rescheduleOffsetDocument({
  minutes, studentId, familyId = null, occurredOn, requestId,
}, serverTimestamp) {
  return documentFromRows(coreSync().rescheduleOffsetDocument(
    Number(minutes), String(studentId), str(familyId), String(occurredOn), String(requestId),
  ), serverTimestamp);
}

/** The charge a settlement writes, or null where a ledger entry would be refused. */
export function rescheduleChargeDocument({
  requestId, amountMinor, occurredOn, familyId = null, studentId, description,
}, serverTimestamp) {
  const rows = coreSync().rescheduleChargeDocument(
    String(requestId), Number(amountMinor), String(occurredOn), str(familyId), String(studentId), String(description || ''),
  );
  return rows.length ? documentFromRows(rows, serverTimestamp) : null;
}
